import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
// --- Types ---
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
  thumbnailUrls?: string[]; // for multi-product bundles
}
interface ToastData {
  name: string;
  city: string;
  product: ScannedProduct;
  timeAgo: string;
}
// --- Data ---
const NAMES = ['Carlos M.', 'Ana P.', 'David R.', 'Laura S.', 'Miguel A.', 'Sofia T.', 'Juan C.', 'Elena G.', 'Diego F.', 'Carmen L.', 'Jose M.', 'Lucia B.', 'Daniel H.', 'Maria V.', 'Alejandro N.', 'Isabella D.', 'Gabriel C.', 'Valentina R.', 'Javier E.', 'Camila O.', 'Mateo L.', 'Valeria C.', 'Lucas M.', 'Martina G.', 'Santiago R.'];
const LATAM_CITIES = ['CDMX, México', 'Guadalajara, México', 'Monterrey, México', 'Bogotá, Colombia', 'Medellín, Colombia', 'Lima, Perú', 'Buenos Aires, Argentina', 'Santiago, Chile', 'Quito, Ecuador', 'Guayaquil, Ecuador', 'San José, Costa Rica', 'Panamá, Panamá', 'Santo Domingo, RD', 'Caracas, Venezuela', 'San Salvador, El Salvador', 'Guatemala, Guatemala', 'Tegucigalpa, Honduras', 'Managua, Nicaragua', 'Asunción, Paraguay', 'Montevideo, Uruguay', 'La Paz, Bolivia', 'Cancún, México', 'Puebla, México', 'Barranquilla, Colombia', 'Cali, Colombia', 'Arequipa, Perú', 'Córdoba, Argentina', 'Valparaíso, Chile'];
// Weighted product catalog: individual + all bundle combos
interface WeightedProduct {
  product: ScannedProduct;
  weight: number; // relative probability
}
const PRODUCT_CATALOG: WeightedProduct[] = [{
  // Solo el manual
  weight: 40,
  product: {
    title: 'Manual del Dinero',
    editionLabel: 'Ebook + Plantillas',
    priceNumber: 6.39,
    priceText: 'US$6.39',
    thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/9KH9NX2Caw88APMuyTmcMh/Manual_del_Dinero.png'
  }
}, {
  // Manual + Ritual de los 3 Sobres
  weight: 20,
  product: {
    title: 'Manual + Ritual de los 3 Sobres',
    editionLabel: 'Ebook + Bonus',
    priceNumber: 15.39,
    priceText: 'US$15.39',
    thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/8KGtPGTXBorbuLXzj8GwSv/ORDER_BUMP_1_Portada_El_Ritual_de_los_3_Sobres.png'
  }
}, {
  // Manual + Auditoría de Fugas
  weight: 15,
  product: {
    title: 'Manual + Auditoría de Fugas',
    editionLabel: 'Ebook + Bonus',
    priceNumber: 15.39,
    priceText: 'US$15.39',
    thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/63YovtKwzidybLWsfdo5NR/ORDER_BUMP_2_Portada_Auditoria_Fugas_Invisibles.png'
  }
}, {
  // Manual + ambos bumps (pack completo)
  weight: 25,
  product: {
    title: 'Manual + Ritual 3 Sobres + Auditoría Fugas Invisibles',
    editionLabel: 'Pack Completo',
    priceNumber: 24.39,
    priceText: 'US$24.39',
    thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/9KH9NX2Caw88APMuyTmcMh/Manual_del_Dinero.png',
    thumbnailUrls: ['https://cdn.magicpatterns.com/uploads/9KH9NX2Caw88APMuyTmcMh/Manual_del_Dinero.png', 'https://cdn.magicpatterns.com/uploads/8KGtPGTXBorbuLXzj8GwSv/ORDER_BUMP_1_Portada_El_Ritual_de_los_3_Sobres.png', 'https://cdn.magicpatterns.com/uploads/63YovtKwzidybLWsfdo5NR/ORDER_BUMP_2_Portada_Auditoria_Fugas_Invisibles.png']
  }
}];
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const recentNamesRef = useRef<string[]>([]);
  const timerRef = useRef<NodeJS.Timeout>();
  // --- Logic ---
  useEffect(() => {
    const getRandomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
    const getNextName = (): string => {
      let availableNames = NAMES.filter(name => !recentNamesRef.current.includes(name));
      if (availableNames.length === 0) {
        recentNamesRef.current = [];
        availableNames = NAMES;
      }
      const selectedName = getRandomItem(availableNames);
      recentNamesRef.current.push(selectedName);
      if (recentNamesRef.current.length > 10) {
        recentNamesRef.current.shift();
      }
      return selectedName;
    };
    const getNextProduct = (): ScannedProduct => {
      // Weighted random selection from catalog
      const totalWeight = PRODUCT_CATALOG.reduce((sum, p) => sum + p.weight, 0);
      let rand = Math.random() * totalWeight;
      for (const entry of PRODUCT_CATALOG) {
        rand -= entry.weight;
        if (rand <= 0) return entry.product;
      }
      return PRODUCT_CATALOG[0].product;
    };
    const getRandomTimeAgo = (): string => {
      const minutes = Math.floor(Math.random() * 28) + 2; // 2–30 min
      return `hace ${minutes} min`;
    };
    const triggerToast = () => {
      const newToast: ToastData = {
        name: getNextName(),
        city: getRandomItem(LATAM_CITIES),
        product: getNextProduct(),
        timeAgo: getRandomTimeAgo()
      };
      setToast(newToast);
      setIsVisible(true);
      // Hide after 4 seconds
      timerRef.current = setTimeout(() => {
        setIsVisible(false);
        scheduleNextToast();
      }, 4000);
    };
    const scheduleNextToast = (isFirst = false) => {
      // First toast after 3s, subsequent between 8-15s
      const delay = isFirst ? 3000 : Math.floor(Math.random() * (15000 - 8000 + 1)) + 8000;
      timerRef.current = setTimeout(() => {
        triggerToast();
      }, delay);
    };
    // Start the cycle
    scheduleNextToast(true);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  // --- Render ---
  return <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-[340px] px-4 pointer-events-none" data-id="element-643">
      <AnimatePresence data-id="element-644">
        {isVisible && toast && <motion.div initial={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 50,
        scale: 0.95
      }} animate={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-white rounded-xl p-3 flex items-center gap-3 pointer-events-auto shadow-lg shadow-black/10 border border-gray-100" style={{
        minWidth: '300px'
      }} data-id="element-645">
            {/* Thumbnail */}
            {toast.product.thumbnailUrls && toast.product.thumbnailUrls.length > 1 ? <div className="shrink-0 w-12 h-12 rounded-lg bg-stone-100 relative flex items-center justify-center overflow-hidden" data-id="element-646">
                {toast.product.thumbnailUrls.map((url, i) => <div key={i} className="absolute rounded-sm overflow-hidden shadow-sm" style={{
            width: '22px',
            height: '28px',
            left: `${4 + i * 8}px`,
            zIndex: i + 1,
            transform: `rotate(${(i - 1) * 5}deg)`,
            top: '50%',
            marginTop: '-14px'
          }} data-id="element-647">
                    <img src={url} alt="" className="w-full h-full object-cover" data-id="element-648" />
                  </div>)}
              </div> : <div className="shrink-0 w-12 h-12 rounded-lg bg-stone-100 overflow-hidden flex items-center justify-center" data-id="element-649">
                <img src={toast.product.thumbnailUrl} alt={toast.product.title} className="w-9 h-11 object-cover rounded-sm" data-id="element-650" />
              </div>}

            {/* Content */}
            <div className="flex-1 min-w-0" data-id="element-651">
              <p className="text-[13px] text-gray-800 leading-tight" data-id="element-652">
                <span className="font-bold" data-id="element-653">{toast.name}</span>{' '}
                <span className="text-gray-500" data-id="element-654">compró</span>
              </p>
              <p className="text-[13px] font-medium text-amber-700 truncate mt-0.5" data-id="element-655">
                {toast.product.title}
              </p>
              <p className="text-[12px] font-semibold text-gray-700 mt-0.5" data-id="element-656">
                {toast.product.priceText}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1" data-id="element-657">
                <span data-id="element-658">📍</span>
                <span data-id="element-659">
                  {toast.city} — {toast.timeAgo}
                </span>
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}